/**
 * Helpers to pull typed content out of an AI SDK UIMessage so presentational
 * components stay dumb (they receive plain strings / arrays, not message parts).
 */

import type { UIMessage } from 'ai';
import { getToolName, isToolUIPart } from 'ai';

export interface SourceCitation {
  sourceId: string;
  url: string;
  title?: string;
}

export function extractText(message: UIMessage): string {
  return message.parts
    .filter((p): p is Extract<typeof p, { type: 'text' }> => p.type === 'text')
    .map((p) => p.text)
    .join('');
}

export function extractReasoning(message: UIMessage): string {
  return message.parts
    .filter((p): p is Extract<typeof p, { type: 'reasoning' }> => p.type === 'reasoning')
    .map((p) => p.text)
    .join('');
}

/**
 * True if the assistant message made a tool call (proposed a draft). Such a
 * turn did something even when it has no visible text, so it must never be
 * treated as an "empty / no response" turn.
 */
export function hasToolParts(message: UIMessage): boolean {
  return message.parts.some((p) => p.type === 'dynamic-tool' || p.type.startsWith('tool-'));
}

const TOOL_LABELS: Record<string, string> = {
  web_search: 'Searching the web',
  propose_create_document: 'Creating a document',
  propose_replace_document: 'Rewriting the document',
  propose_patch_document: 'Editing the document',
  propose_create_slides: 'Creating slides',
  propose_replace_slides: 'Rewriting the slides',
  propose_patch_slides: 'Editing the slides',
  propose_create_unit: 'Adding a unit',
  propose_create_lesson: 'Adding a lesson',
  propose_patch_course: 'Editing the course',
  propose_patch_unit: 'Editing a unit',
  propose_patch_lesson: 'Editing a lesson',
  propose_delete_entity: 'Removing an item',
};

function humanizeToolName(name: string): string {
  const base = name.replace(/^propose_/, '').replace(/_/g, ' ');
  return base.charAt(0).toUpperCase() + base.slice(1);
}

export type ToolActivityStatus = 'running' | 'done' | 'error';

export interface ToolActivityItem {
  key: string;
  name: string;
  label: string;
  status: ToolActivityStatus;
}

/**
 * Live tool/web-search activity derived from the assistant message's tool
 * parts. The backend only emits a coarse "Thinking…" status, so this is what
 * makes "searching the web" / "creating a document" visible to the user.
 */
export function extractToolActivity(message: UIMessage): ToolActivityItem[] {
  const out: ToolActivityItem[] = [];
  message.parts.forEach((part, index) => {
    if (!isToolUIPart(part)) return;
    const name = getToolName(part);
    const meta = part as unknown as { state?: string; toolCallId?: string };
    const state = meta.state ?? 'input-available';
    const status: ToolActivityStatus =
      state === 'output-error' ? 'error' : state === 'output-available' ? 'done' : 'running';
    out.push({
      key: meta.toolCallId ?? `${name}-${index}`,
      name,
      label: TOOL_LABELS[name] ?? humanizeToolName(name),
      status,
    });
  });
  return out;
}

export function extractSources(message: UIMessage): SourceCitation[] {
  const seen = new Set<string>();
  const out: SourceCitation[] = [];
  for (const part of message.parts) {
    if (part.type !== 'source-url') continue;
    const p = part as { sourceId?: string; url?: string; title?: string };
    if (!p.url || seen.has(p.url)) continue;
    seen.add(p.url);
    out.push({
      sourceId: p.sourceId ?? p.url,
      url: p.url,
      title: p.title,
    });
  }
  return out;
}
